import axios from 'axios'

/**
 * 管理画面用お知らせストア
 *
 * APIエンドポイント設計（バックエンド実装時の参考）:
 * GET    /api/admin/announcements         - お知らせ一覧取得
 * POST   /api/admin/announcements         - お知らせ作成
 * PUT    /api/admin/announcements/:id     - お知らせ更新
 * DELETE /api/admin/announcements/:id     - お知らせ削除
 *
 * データベース設計（参考）:
 * announcements テーブル:
 * - id (PRIMARY KEY)
 * - title (VARCHAR(100) NOT NULL)
 * - content (TEXT NOT NULL)
 * - target_student_id (INT, FOREIGN KEY)
 * - created_at (DATETIME)
 * - updated_at (DATETIME)
 *
 * announcement_reads テーブル:
 * - announcement_id (FOREIGN KEY)
 * - student_id (FOREIGN KEY)
 * - is_read (BOOLEAN DEFAULT false)
 * - read_at (DATETIME NULL)
 */

/**
 * バックエンドから返されるお知らせデータをフロントエンド用に正規化
 * @param {Object} announcement - バックエンドからのお知らせデータ
 * @returns {Object} 正規化されたお知らせデータ
 */
const normalizeAnnouncement = (announcement) => ({
	id: announcement.id,
	title: announcement.title,
	content: announcement.content,
	targetStudent: announcement.target_student_id,
	createdAt: announcement.created_at,
	updatedAt: announcement.updated_at
})

export const adminAnnouncement = {
	namespaced: true,
	state: {
		announcements: [],
		loading: false,
		totalCount: 0,
		totalUnreadCount: 0
	},
	getters: {
		loading: (state) => state.loading,
		announcements: (state) => state.announcements,
		totalCount: (state) => state.totalCount,
		totalUnreadCount: (state) => state.totalUnreadCount,
		// 有効なお知らせのみを取得
		activeAnnouncements: (state) => {
			return state.announcements.filter((announcement) => announcement.isActive)
		},
		// カテゴリ別の統計
		announcementsByType: (state) => {
			return state.announcements.reduce((acc, announcement) => {
				if (!acc[announcement.type]) {
					acc[announcement.type] = 0
				}
				acc[announcement.type]++
				return acc
			}, {})
		}
	},
	mutations: {
		SET_LOADING(state, loading) {
			state.loading = loading
		},
		SET_ANNOUNCEMENTS(state, announcements) {
			// バックエンドから返されるデータをフロントエンド用に正規化
			state.announcements = announcements.map(normalizeAnnouncement)
			state.totalCount = announcements.length
			state.totalUnreadCount = announcements.reduce((sum, item) => sum + (item.unreadCount || 0), 0)
			state.loading = false
		},
		ADD_ANNOUNCEMENT(state, announcement) {
			// 新しいお知らせも正規化関数を使用
			const normalizedAnnouncement = normalizeAnnouncement(announcement)
			state.announcements.unshift(normalizedAnnouncement)
			state.totalCount++
		},
		UPDATE_ANNOUNCEMENT(state, updatedAnnouncement) {
			const index = state.announcements.findIndex((a) => a.id === updatedAnnouncement.id)
			if (index !== -1) {
				// 更新されたお知らせも正規化関数を使用
				const normalizedAnnouncement = normalizeAnnouncement(updatedAnnouncement)
				state.announcements.splice(index, 1, normalizedAnnouncement)
			}
		},
		REMOVE_ANNOUNCEMENT(state, announcementId) {
			state.announcements = state.announcements.filter((a) => a.id !== announcementId)
			state.totalCount--
		},
		UPDATE_UNREAD_COUNT(state, { announcementId, unreadCount }) {
			const announcement = state.announcements.find((a) => a.id === announcementId)
			if (announcement) {
				announcement.unreadCount = unreadCount
				state.totalUnreadCount = state.announcements.reduce((sum, item) => sum + (item.unreadCount || 0), 0)
			}
		}
	},
	actions: {
		/**
		 * お知らせ一覧を取得
		 * @param {Object} payload - { accessToken: string }
		 */
		async fetchAnnouncements({ commit }, payload) {
			commit('SET_LOADING', true)

			try {
				const response = await axios.get('/api/admin/announcements', {
					headers: {
						Authorization: `Bearer ${payload.accessToken}`
					}
				})
				commit('SET_ANNOUNCEMENTS', response.data.announcements)
				commit('SET_LOADING', false)
			} catch (error) {
				commit('SET_LOADING', false)
				console.error('お知らせ一覧の取得に失敗しました:', error)
				throw error
			}
		},

		/**
		 * お知らせを作成
		 * @param {Object} payload - { announcement: Object, accessToken: string }
		 */
		async createAnnouncement({ commit }, payload) {
			commit('SET_LOADING', true)

			try {
				const response = await axios.post(
					'/api/admin/announcements',
					{
						title: payload.announcement.title,
						content: payload.announcement.content,
						target_student_id: payload.announcement.targetStudent
					},
					{
						headers: {
							Authorization: `Bearer ${payload.accessToken}`
						}
					}
				)
				commit('ADD_ANNOUNCEMENT', response.data.announcement)
				commit('SET_LOADING', false)
			} catch (error) {
				commit('SET_LOADING', false)
				console.error('お知らせの作成に失敗しました:', error)
				throw error
			}
		},

		/**
		 * お知らせを更新
		 * @param {Object} payload - { announcement: Object, accessToken: string }
		 */
		async updateAnnouncement({ commit }, payload) {
			commit('SET_LOADING', true)

			try {
				const response = await axios.put(
					`/api/admin/announcements/${payload.announcement.id}`,
					{
						title: payload.announcement.title,
						content: payload.announcement.content,
						target_student_id: payload.announcement.targetStudent
					},
					{
						headers: {
							Authorization: `Bearer ${payload.accessToken}`
						}
					}
				)
				commit('UPDATE_ANNOUNCEMENT', response.data.announcement)
				commit('SET_LOADING', false)
			} catch (error) {
				commit('SET_LOADING', false)
				console.error('お知らせの更新に失敗しました:', error)
				throw error
			}
		},

		/**
		 * お知らせを削除
		 * @param {Object} payload - { announcementId: number, accessToken: string }
		 */
		async deleteAnnouncement({ commit }, payload) {
			commit('SET_LOADING', true)

			try {
				await axios.delete(`/api/admin/announcements/${payload.announcementId}`, {
					headers: {
						Authorization: `Bearer ${payload.accessToken}`
					}
				})
				commit('REMOVE_ANNOUNCEMENT', payload.announcementId)
				commit('SET_LOADING', false)
			} catch (error) {
				commit('SET_LOADING', false)
				console.error('お知らせの削除に失敗しました:', error)
				throw error
			}
		},

		/**
		 * 特定のお知らせの統計情報を更新
		 * @param {Object} payload - { announcementId: number, accessToken: string }
		 */
		async refreshAnnouncementStats({ commit }, payload) {
			try {
				const response = await axios.get(`/api/admin/announcements/${payload.announcementId}/stats`, {
					headers: {
						Authorization: `Bearer ${payload.accessToken}`
					}
				})
				commit('UPDATE_UNREAD_COUNT', {
					announcementId: payload.announcementId,
					unreadCount: response.data.unread_count
				})
			} catch (error) {
				console.error('お知らせ統計の更新に失敗しました:', error)
			}
		}
	}
}
